import { useState } from 'react';
import { toast } from '@/components';
import { getApiErrorMessage } from '@/utils/error-messages';

interface UseAsyncActionOptions<TArgs extends unknown[]> {
    successMessage?: string;
    errorMessage?: string;
    onError?: (error: unknown, ...args: TArgs) => void;
}

interface UseAsyncActionResult<TArgs extends unknown[]> {
    isSubmitting: boolean;
    run: (...args: TArgs) => Promise<void>;
}

export function useAsyncAction<TArgs extends unknown[] = []>(
    action: (...args: TArgs) => Promise<void>,
    options: UseAsyncActionOptions<TArgs> = {},
): UseAsyncActionResult<TArgs> {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const run = async (...args: TArgs) => {
        setIsSubmitting(true);
        try {
            await action(...args);
            if (options.successMessage) {
                toast.success(options.successMessage);
            }
        } catch (error) {
            if (options.onError) {
                options.onError(error, ...args);
            } else if (options.errorMessage) {
                toast.error(getApiErrorMessage(error, options.errorMessage));
            } else {
                throw error;
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return { isSubmitting, run };
}
